import { AxisAlignedBoundingBox, Cartesian2, Cartesian3, Color, } from "cesium";
import { EarthBrainUtils } from "../utilities/earthbrainutility";
import { getDelaunayTriangulation } from "../utilities/extra-utils";
import { HeightMapUtils } from ".//utils";
import { TransformationUtil } from "../utilities/transformation-utils";
export const DEFAULT_HEIGHT_MAP_BOX_SIZE = 0.5;
// ToDo-2965: Should there be a dispose function for heightmaps?
export class HeightMap {
    _boundingBox;
    _info;
    _primitive = undefined;
    static createFromBox(box, voxelSize) {
        const heightMap = new HeightMap();
        heightMap._boundingBox = box;
        const halfDiagonal = Cartesian3.subtract(heightMap._boundingBox.maximum, heightMap._boundingBox.center, new Cartesian3());
        const minIndexes = [
            Math.floor(heightMap._boundingBox.center.x) - Math.ceil(halfDiagonal.x),
            Math.floor(heightMap._boundingBox.center.y) - Math.ceil(halfDiagonal.y),
            Math.floor(heightMap._boundingBox.center.z) - Math.ceil(halfDiagonal.z),
        ].map((c) => c / voxelSize);
        const maxIndexes = [
            Math.floor(heightMap._boundingBox.center.x) + Math.ceil(halfDiagonal.x),
            Math.floor(heightMap._boundingBox.center.y) + Math.ceil(halfDiagonal.y),
            Math.floor(heightMap._boundingBox.center.z) + Math.ceil(halfDiagonal.z),
        ].map((c) => c / voxelSize);
        const info = {
            boxMin: Cartesian3.pack(heightMap._boundingBox.minimum, []),
            boxMax: Cartesian3.pack(heightMap._boundingBox.maximum, []),
            boxesInX: Math.floor(2 * Math.ceil(halfDiagonal.x / voxelSize)),
            boxesInY: Math.floor(2 * Math.ceil(halfDiagonal.y / voxelSize)),
            minIndexes,
            maxIndexes,
            boxSize: voxelSize,
            heights: new Float32Array(),
        };
        heightMap._info = info;
        return heightMap;
    }
    static FromInput(input) {
        // Get The grid size
        // Copied from server side
        const box = AxisAlignedBoundingBox.fromPoints([
            Cartesian3.fromArray(input.min),
            Cartesian3.fromArray(input.max),
        ]);
        const heightMap = this.createFromBox(box, input.size);
        heightMap._info.heights = new Float32Array(input.heights);
        if (!input?.version || input.version <= 1.0) {
            heightMap.postProcessInterpolation();
            // ToDo: Check why this has been called 2 times
            heightMap.postProcessInterpolation();
        }
        return heightMap;
    }
    static createFromBoxForIndexedGeometry(box, voxelSize) {
        const heightMap = new HeightMap();
        heightMap._boundingBox = box;
        const halfDiagonal = Cartesian2.subtract(heightMap._boundingBox.maximum, heightMap._boundingBox.center, new Cartesian3());
        const minIndexes = [
            heightMap._boundingBox.center.x - halfDiagonal.x,
            heightMap._boundingBox.center.y - halfDiagonal.y,
        ].map((c) => Math.floor(c / voxelSize));
        const maxIndexes = [
            heightMap._boundingBox.center.x + halfDiagonal.x,
            heightMap._boundingBox.center.y + halfDiagonal.y,
        ].map((c) => Math.ceil(c / voxelSize));
        const info = {
            boxMin: Cartesian3.pack(heightMap._boundingBox.minimum, []),
            boxMax: Cartesian3.pack(heightMap._boundingBox.maximum, []),
            boxesInX: maxIndexes[0] - minIndexes[0],
            boxesInY: maxIndexes[1] - minIndexes[1],
            minIndexes,
            maxIndexes,
            boxSize: voxelSize,
            heights: new Float32Array(),
        };
        heightMap._info = info;
        return heightMap;
    }
    // ToDo-2965: What should happen in this function is geom is undfined
    static FromIndexedGeometry(geometry, voxelSize) {
        const tTriangleBox = AxisAlignedBoundingBox.fromPoints(Cartesian3.unpackArray(geometry.vertices));
        const heightMap = this.createFromBoxForIndexedGeometry(tTriangleBox, voxelSize);
        heightMap._info.heights = new Float32Array(heightMap._info.boxesInX * heightMap._info.boxesInY);
        heightMap._info.heights.forEach((h, i) => (heightMap._info.heights[i] = Number.NaN));
        for (let i = 0; i < geometry.indices.length; i += 3) {
            const p0 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i] * 3);
            const p1 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 1] * 3);
            const p2 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 2] * 3);
            const tTriangleBox = AxisAlignedBoundingBox.fromPoints([p0, p1, p2]);
            const tBoxMin = new Cartesian3(tTriangleBox.minimum.x, tTriangleBox.minimum.y, 0);
            const tBoxMax = new Cartesian3(tTriangleBox.maximum.x, tTriangleBox.maximum.y, 0);
            const tMinX = HeightMapUtils.getIndexForCoordinate(tBoxMin.x, voxelSize);
            const tMinY = HeightMapUtils.getIndexForCoordinate(tBoxMin.y, voxelSize);
            const tMaxX = HeightMapUtils.getIndexForCoordinate(tBoxMax.x, voxelSize);
            const tMaxY = HeightMapUtils.getIndexForCoordinate(tBoxMax.y, voxelSize);
            // Loop through the voxel found inside the bounding box of this triangle
            for (let xIndex = tMinX; xIndex <= tMaxX; ++xIndex) {
                for (let yIndex = tMinY; yIndex <= tMaxY; ++yIndex) {
                    const voxelCenter = HeightMapUtils.getCenter(xIndex, yIndex, false, heightMap._info);
                    const weights = HeightMapUtils.getBarycentricPointWeights(p0, p1, p2, voxelCenter);
                    const isOutside = weights.some((w) => w < 0 || w > 1);
                    const index = HeightMapUtils.getArrayIndex(xIndex, yIndex, heightMap._info);
                    if (!isOutside && index !== undefined) {
                        const calculatedHeight = weights[0] * p0.z + weights[1] * p1.z + weights[2] * p2.z;
                        heightMap._info.heights[index] = calculatedHeight;
                    }
                    //edge condition
                    const p0IndexXY = HeightMapUtils.getIndexForPoint(p0, voxelSize);
                    const p0Index = HeightMapUtils.getArrayIndex(p0IndexXY.xIndex, p0IndexXY.yIndex, heightMap._info);
                    if (p0Index !== undefined) {
                        heightMap._info.heights[p0Index] = p0.z;
                    }
                    const p1IndexXY = HeightMapUtils.getIndexForPoint(p1, voxelSize);
                    const p1Index = HeightMapUtils.getArrayIndex(p1IndexXY.xIndex, p1IndexXY.yIndex, heightMap._info);
                    if (p1Index !== undefined) {
                        heightMap._info.heights[p1Index] = p1.z;
                    }
                    const p2IndexXY = HeightMapUtils.getIndexForPoint(p2, voxelSize);
                    const p2Index = HeightMapUtils.getArrayIndex(p2IndexXY.xIndex, p2IndexXY.yIndex, heightMap._info);
                    if (p2Index !== undefined) {
                        heightMap._info.heights[p2Index] = p2.z;
                    }
                }
            }
        }
        return heightMap;
    }
    /**
     * This geometry ignores the holes and complex shapes
     */
    get geometry() {
        const centers = this.getVoxelCenters(true).filter((c) => isFinite(c.z) && c.z !== 0);
        const geometry = getDelaunayTriangulation(centers, new Cartesian3(0, 0, 1));
        return geometry;
    }
    get boxSize() {
        return this._info.boxSize;
    }
    async renderTopAndBottomPlanes(viewer, site) {
        const bottomPlanePoints = await TransformationUtil.transformLocalPointsToWGS84InBatches([
            new Cartesian3(this._boundingBox.minimum.x, this._boundingBox.minimum.y, this._boundingBox.minimum.z),
            new Cartesian3(this._boundingBox.maximum.x, this._boundingBox.minimum.y, this._boundingBox.minimum.z),
            new Cartesian3(this._boundingBox.minimum.x, this._boundingBox.maximum.y, this._boundingBox.minimum.z),
            new Cartesian3(this._boundingBox.maximum.x, this._boundingBox.maximum.y, this._boundingBox.minimum.z),
        ], site);
        const bottomPlane = EarthBrainUtils.createEntityFromIndexedGeometry({
            indices: [0, 1, 2, 1, 3, 2],
            vertices: Cartesian3.packArray(bottomPlanePoints),
        }, Color.fromCssColorString("blue").withAlpha(0.5));
        viewer.scene.primitives.add(bottomPlane);
        const topPlanePoints = await TransformationUtil.transformLocalPointsToWGS84InBatches([
            new Cartesian3(this._boundingBox.minimum.x, this._boundingBox.minimum.y, this._boundingBox.maximum.z),
            new Cartesian3(this._boundingBox.maximum.x, this._boundingBox.minimum.y, this._boundingBox.maximum.z),
            new Cartesian3(this._boundingBox.minimum.x, this._boundingBox.maximum.y, this._boundingBox.maximum.z),
            new Cartesian3(this._boundingBox.maximum.x, this._boundingBox.maximum.y, this._boundingBox.maximum.z),
        ], site);
        const topPlane = EarthBrainUtils.createEntityFromIndexedGeometry({
            indices: [0, 1, 2, 1, 3, 2],
            vertices: Cartesian3.packArray(topPlanePoints),
        }, Color.fromCssColorString("brown").withAlpha(0.5));
        viewer.scene.primitives.add(topPlane);
    }
    async render(color, site, viewer) {
        const centers = this.getVoxelCenters(true).filter((c) => isFinite(c.z) && c.z !== 0);
        const worldPositions = await TransformationUtil.transformLocalPointsToWGS84InBatches(centers, site);
        if (worldPositions.length === 0)
            throw new Error("height map is empty");
        const localTriangulation = getDelaunayTriangulation(centers, new Cartesian3(0, 0, 1));
        this._primitive && this.dispose(viewer);
        this._primitive = EarthBrainUtils.createEntityFromIndexedGeometry({
            indices: localTriangulation.indices,
            vertices: Cartesian3.packArray(worldPositions),
        }, Color.fromCssColorString(color), undefined, true);
        viewer.scene.primitives.add(this._primitive);
    }
    dispose(viewer) {
        viewer.scene.primitives.remove(this._primitive);
        this._primitive = undefined;
    }
    isHeightMapPrimitiveExists() {
        return this._primitive !== undefined;
    }
    // This object can be used to pass to workers. Since class objects are not shared
    getObject() {
        return this._info;
    }
    getVoxelCenters(includeHeight) {
        const centers = [];
        for (let xIndex = this._info.minIndexes[0]; xIndex < this._info.maxIndexes[0]; ++xIndex) {
            for (let yIndex = this._info.minIndexes[1]; yIndex < this._info.maxIndexes[1]; ++yIndex) {
                centers.push(HeightMapUtils.getCenter(xIndex, yIndex, includeHeight, this._info));
            }
        }
        return centers;
    }
    rayCast(ray) {
        if (!EarthBrainUtils.isValidRay(ray)) {
            console.warn("Invalid ray");
            return undefined;
        }
        const res = EarthBrainUtils.intersectBoundingBox(ray, this._boundingBox);
        // Does not intersect with the heightmap box
        if (!res.start && !res.end)
            return undefined;
        // Start/End points not necessarily will be above/below the pointcloud
        // We need to check for the first valid point above/below and begin checking from there
        const rayStartPoint = res.start
            ? EarthBrainUtils.getCartesianPointAtDistanceFromCartesian(ray.origin, ray.direction, res.start - 1)
            : ray.origin;
        // Making sure that end point is just outside of the bounding box
        const rayEndPoint = EarthBrainUtils.getCartesianPointAtDistanceFromCartesian(ray.origin, ray.direction, res.end + 1);
        const dir2d = Cartesian2.subtract(rayEndPoint, rayStartPoint, new Cartesian2());
        if (dir2d.equals(Cartesian2.ZERO)) {
            const height = HeightMapUtils.getHeight(rayStartPoint, this._info);
            if (isNaN(height))
                return undefined;
            if ((height - ray.origin.z) / ray.direction.z > 0) {
                return new Cartesian3(rayStartPoint.x, rayStartPoint.y, height);
            }
            else {
                return undefined;
            }
        }
        Cartesian2.normalize(dir2d, dir2d);
        const dir3d = EarthBrainUtils.getDirBetweenTwoCartesians(rayStartPoint, rayEndPoint);
        const angle = Cartesian3.angleBetween(dir3d, new Cartesian3(dir2d.x, dir2d.y, 0));
        const cosine = Math.cos(angle);
        //Interpolate on XY plane
        const interpolationPitch = this.boxSize * 0.5;
        const interpolatedPoints = [];
        const dist = Cartesian2.distance(rayStartPoint, rayEndPoint);
        const divisions = Math.floor(dist / interpolationPitch);
        interpolatedPoints.push(rayStartPoint);
        //get the interpolation points
        for (let i = 1; i <= divisions; ++i) {
            const dist3dFromStart = (i * interpolationPitch) / cosine;
            const point = Cartesian3.add(rayStartPoint, Cartesian3.multiplyByScalar(dir3d, dist3dFromStart, new Cartesian3()), new Cartesian3());
            interpolatedPoints.push(point);
        }
        interpolatedPoints.push(rayEndPoint);
        let intersectionPoint = undefined;
        let initialHeight;
        let index = 0;
        while (index < interpolatedPoints.length) {
            initialHeight = HeightMapUtils.getHeight(interpolatedPoints[index], this._info);
            if (initialHeight !== undefined && !isNaN(initialHeight))
                break;
            ++index;
        }
        if (initialHeight !== undefined && !isNaN(initialHeight)) {
            const initialSign = Math.sign(interpolatedPoints[index].z - initialHeight);
            let previousValidIndex = index;
            for (let i = index; i < interpolatedPoints.length; ++i) {
                const height = HeightMapUtils.getHeight(interpolatedPoints[i], this._info);
                if (isNaN(height))
                    continue;
                const sign = Math.sign(interpolatedPoints[i].z - height);
                if (initialSign !== sign) {
                    //At this point we know intersection point lies between interpolatedPoints[i] and interpolatedPoints[previousValidIndex]
                    //Using linear algebra, geometry and similar triangle
                    const prevHeightDiff = Math.abs(HeightMapUtils.getHeight(interpolatedPoints[previousValidIndex], this._info) - interpolatedPoints[previousValidIndex].z);
                    const currHeightDiff = Math.abs(HeightMapUtils.getHeight(interpolatedPoints[i], this._info) -
                        interpolatedPoints[i].z);
                    const distance = Cartesian3.distance(interpolatedPoints[previousValidIndex], interpolatedPoints[i]);
                    const distToIntersection = (distance * prevHeightDiff) / (prevHeightDiff + currHeightDiff);
                    intersectionPoint = Cartesian3.add(interpolatedPoints[previousValidIndex], Cartesian3.multiplyByScalar(ray.direction, distToIntersection, new Cartesian3()), new Cartesian3());
                    break;
                }
                previousValidIndex = i;
            }
        }
        if (intersectionPoint &&
            (isNaN(intersectionPoint.x) ||
                isNaN(intersectionPoint.y) ||
                isNaN(intersectionPoint.z))) {
            return undefined;
        }
        return intersectionPoint;
    }
    // Contains NaN entries
    traceHeights(points) {
        const heightArray = points.map((p) => HeightMapUtils.getHeight(p, this._info));
        return heightArray;
    }
    // Contains undefined entries
    tracePoints(points) {
        const outline = points.map((p) => {
            const height = HeightMapUtils.getHeight(p, this._info);
            return isFinite(height) ? new Cartesian3(p.x, p.y, height) : undefined;
        });
        return outline;
    }
    postProcessInterpolation(tolerance = 1.5) {
        const toleranceIndexRange = Math.ceil(tolerance / 0.5);
        for (let xIndex = this._info.minIndexes[0]; xIndex <= this._info.maxIndexes[0]; ++xIndex) {
            for (let yIndex = this._info.minIndexes[1]; yIndex <= this._info.maxIndexes[1]; ++yIndex) {
                const point = HeightMapUtils.getCenter(xIndex, yIndex, true, this._info);
                if (isNaN(point.z) || point.z === undefined) {
                    let leftPoint, rightPoint, bottomPoint, topPoint;
                    for (let i = 1; i <= toleranceIndexRange; ++i) {
                        leftPoint = HeightMapUtils.getCenter(xIndex - i, yIndex, true, this._info);
                        if (!isNaN(leftPoint.z)) {
                            break;
                        }
                        else {
                            leftPoint = undefined;
                        }
                    }
                    for (let i = 1; i <= toleranceIndexRange; ++i) {
                        rightPoint = HeightMapUtils.getCenter(xIndex + i, yIndex, true, this._info);
                        if (!isNaN(rightPoint.z)) {
                            break;
                        }
                        else {
                            rightPoint = undefined;
                        }
                    }
                    for (let i = 1; i <= toleranceIndexRange; ++i) {
                        bottomPoint = HeightMapUtils.getCenter(xIndex, yIndex - i, true, this._info);
                        if (!isNaN(bottomPoint.z)) {
                            break;
                        }
                        else {
                            bottomPoint = undefined;
                        }
                    }
                    for (let i = 1; i <= toleranceIndexRange; ++i) {
                        topPoint = HeightMapUtils.getCenter(xIndex, yIndex + i, true, this._info);
                        if (!isNaN(topPoint.z)) {
                            break;
                        }
                        else {
                            topPoint = undefined;
                        }
                    }
                    if ((leftPoint && rightPoint) || (bottomPoint && topPoint)) {
                        let horizontalDistance = Number.NaN;
                        let verticalDistance = Number.NaN;
                        if (leftPoint && rightPoint) {
                            horizontalDistance = Cartesian2.distance(leftPoint, rightPoint);
                        }
                        if (bottomPoint && topPoint) {
                            verticalDistance = Cartesian2.distance(bottomPoint, topPoint);
                        }
                        if (!isNaN(horizontalDistance) && !isNaN(verticalDistance)) {
                            if (horizontalDistance < verticalDistance) {
                                const interPolationDist = Cartesian2.distance(leftPoint, point);
                                const height = leftPoint.z +
                                    (interPolationDist / horizontalDistance) *
                                        (rightPoint.z - leftPoint.z);
                                point.z = height;
                            }
                            else {
                                const interPolationDist = Cartesian2.distance(bottomPoint, point);
                                const height = bottomPoint.z +
                                    (interPolationDist / verticalDistance) *
                                        (topPoint.z - bottomPoint.z);
                                point.z = height;
                            }
                        }
                        else if (!isNaN(horizontalDistance)) {
                            const interPolationDist = Cartesian2.distance(leftPoint, point);
                            const height = leftPoint.z +
                                (interPolationDist / horizontalDistance) *
                                    (rightPoint.z - leftPoint.z);
                            point.z = height;
                        }
                        else if (!isNaN(verticalDistance)) {
                            const interPolationDist = Cartesian2.distance(bottomPoint, point);
                            const height = bottomPoint.z +
                                (interPolationDist / verticalDistance) *
                                    (topPoint.z - bottomPoint.z);
                            point.z = height;
                        }
                        const heightIndex = HeightMapUtils.getArrayIndex(xIndex, yIndex, this._info);
                        this._info.heights[heightIndex] = point.z;
                    }
                }
            }
        }
    }
    updateHeightOfBoundingBox() {
        let minHeight = Number.POSITIVE_INFINITY;
        let maxHeight = Number.NEGATIVE_INFINITY;
        for (const h of this._info.heights) {
            if (h !== undefined && !isNaN(h)) {
                if (h < minHeight)
                    minHeight = h;
                if (h > maxHeight)
                    maxHeight = h;
            }
        }
        if (minHeight !== Number.POSITIVE_INFINITY) {
            this._boundingBox.minimum.z = minHeight;
        }
        if (maxHeight !== Number.NEGATIVE_INFINITY) {
            this._boundingBox.maximum.z = maxHeight;
        }
    }
}
//# sourceMappingURL=height-map.js.map